import { makeid } from 'utils/helpers/makeid';

import ScriptWrapper from './script-wrapper';
import { addField, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createSuicideHomicideTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth. For quality and
          compliance purposes, this call may be recorded. To make sure I'm
          speaking with the right person, can you please confirm your full name
          and date of birth?" If caregiver present: "Are you the patient's
          caregiver? Do I have the patient's consent to speak with you today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'patientDisclosureOfHarmRisk',
      type: 'checkbox',
      title: <p>Patient Disclosure of Harm Risk</p>,
      description: (
        <ScriptWrapper>
          "Thank you for telling me that. I'm really glad you reached out and
          shared this with me. I want you to know I'm here with you, and my
          priority right now is your safety."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Suicidal thoughts disclosed',
          value: 'suicidalThoughtsDisclosed',
        },
        {
          key: makeid(10),
          label: 'Homicidal thoughts disclosed',
          value: 'homicidalThoughtsDisclosed',
        },
        {
          key: makeid(10),
          label: 'Statements indicating risk',
          value: 'statementsIndicatingRisk',
        },
        {
          key: makeid(10),
          label: 'Disclosure without specific details',
          value: 'disclosureWithoutSpecificDetails',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'immediacyOfRiskAssessed',
      type: 'radio',
      title: <p>Immediacy of Risk Assessed</p>,
      description: (
        <ScriptWrapper>
          "Based on what you just shared, are you feeling at risk of hurting
          yourself or someone else right now?" (Acknowledge response without
          debate.)
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Immediate risk',
          value: 'immediateRisk',
        },
        {
          key: makeid(10),
          label: 'Ongoing distress without immediate action',
          value: 'ongoingDistressWithoutImmediateAction',
        },
        {
          key: makeid(10),
          label: 'Risk unclear but concerning',
          value: 'riskUnclearButConcerning',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'patientLocationConfirmed',
      type: 'radio',
      title: <p>Patient Location Confirmed</p>,
      description: (
        <ScriptWrapper>
          "To help keep you safe, I need to know where you are right now. Can
          you tell me your current location, including the address if you know
          it?" If unsure: "That's okay — are you at home, with someone, or
          somewhere else?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Location confirmed',
          value: 'locationConfirmed',
        },
        {
          key: makeid(10),
          label: 'Partial location obtained',
          value: 'partialLocationObtained',
        },
        {
          key: makeid(10),
          label: 'Unable to obtain location',
          value: 'unableToObtainLocation',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'patientEngagementMaintained',
      type: 'radio',
      title: <p>Patient Engagement Maintained</p>,
      description: (
        <ScriptWrapper>
          "Thank you for staying on the phone with me. You're not alone right
          now — I'm here with you. We're going to focus on getting you through
          this moment safely."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient remained on line',
          value: 'patientRemainedOnLine',
        },
        {
          key: makeid(10),
          label: 'Patient intermittently responsive',
          value: 'patientIntermittentlyResponsive',
        },
        {
          key: makeid(10),
          label: 'Call disconnected unexpectedly',
          value: 'callDisconnectedUnexpectedly',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'emergencyResponseInitiated',
      type: 'checkbox',
      title: <p>Emergency Response Initiated</p>,
      description: (
        <ScriptWrapper>
          "Because of what you shared, I'm concerned about your safety. To help
          keep you safe, I'm going to have emergency services come to your
          location. If you can, please stay on the phone with me while help is
          on the way."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Supervisor notified',
          value: 'supervisorNotified',
        },
        {
          key: makeid(10),
          label: 'Teammate contacted 911',
          value: 'teammateContacted911',
        },
        {
          key: makeid(10),
          label: 'Emergency services dispatched',
          value: 'emergencyServicesDispatched',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'safetySupportProvided',
      type: 'checkbox',
      title: <p>Safety Support Provided</p>,
      description: (
        <ScriptWrapper>
          "If you're able, can you move to a safer place right now? Is there
          someone nearby you trust who can be with you? If there's anything
          around you that could be used to hurt yourself or someone else, can
          you move away from it if you're able?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Encouraged safer environment',
          value: 'encouragedSaferEnvironment',
        },
        {
          key: makeid(10),
          label: 'Encouraged support person presence',
          value: 'encouragedSupportPersonPresence',
        },
        {
          key: makeid(10),
          label: 'Encouraged distancing from means',
          value: 'encouragedDistancingFromMeans',
        },
        {
          key: makeid(10),
          label: 'Not appropriate / not possible',
          value: 'notAppropriateNotPossible',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'callOutcome',
      type: 'radio',
      title: <p>Call Outcome</p>,
      description: (
        <ScriptWrapper>
          "I'm still here with you. Help is on the way. You did the right thing
          by telling someone."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Emergency services arrived',
          value: 'emergencyServicesArrived',
        },
        {
          key: makeid(10),
          label: 'Call transferred',
          value: 'callTransferred',
        },
        {
          key: makeid(10),
          label: 'Call disconnected prior to arrival',
          value: 'callDisconnectedPriorToArrival',
        },
      ],
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TSuicideHomicideTemplateValues = ExtractStepValues<
  ReturnType<typeof createSuicideHomicideTemplate>
>;

export const createSuicideHomicideBlurb = (
  data: TSuicideHomicideTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    patientDisclosureOfHarmRisk,
    immediacyOfRiskAssessed,
    patientLocationConfirmed,
    patientEngagementMaintained,
    emergencyResponseInitiated,
    safetySupportProvided,
    callOutcome,
  } = data;
  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    patientDisclosureOfHarmRisk?.length &&
    !hasField(ctx, 'patientDisclosureOfHarmRisk')
  ) {
    addField(ctx, 'patientDisclosureOfHarmRisk');
    const map = {
      suicidalThoughtsDisclosed: 'Patient disclosed suicidal thoughts.',
      homicidalThoughtsDisclosed: 'Patient disclosed homicidal thoughts.',
      statementsIndicatingRisk: 'Patient made statements indicating risk.',
      disclosureWithoutSpecificDetails:
        'Patient disclosed distress without specific details.',
    };
    txt.push(...mapValuesToText(patientDisclosureOfHarmRisk, map));
  }

  if (immediacyOfRiskAssessed && !hasField(ctx, 'immediacyOfRiskAssessed')) {
    addField(ctx, 'immediacyOfRiskAssessed');
    const map = {
      immediateRisk: 'Immediate risk assessed.',
      ongoingDistressWithoutImmediateAction:
        'Ongoing distress noted without immediate danger.',
      riskUnclearButConcerning: 'Risk unclear but concerning.',
    };
    const line = map[immediacyOfRiskAssessed];
    if (line) txt.push(line);
  }

  if (patientLocationConfirmed && !hasField(ctx, 'patientLocationConfirmed')) {
    addField(ctx, 'patientLocationConfirmed');
    const map = {
      locationConfirmed: 'Patient location confirmed.',
      partialLocationObtained: 'Partial patient location obtained.',
      unableToObtainLocation: 'Unable to obtain patient location.',
    };
    const line = map[patientLocationConfirmed];
    if (line) txt.push(line);
  }

  if (
    patientEngagementMaintained &&
    !hasField(ctx, 'patientEngagementMaintained')
  ) {
    addField(ctx, 'patientEngagementMaintained');
    const map = {
      patientRemainedOnLine: 'Patient remained on the line.',
      patientIntermittentlyResponsive: 'Patient intermittently responsive.',
      callDisconnectedUnexpectedly: 'Call disconnected unexpectedly.',
    };
    const line = map[patientEngagementMaintained];
    if (line) txt.push(line);
  }

  if (
    emergencyResponseInitiated?.length &&
    !hasField(ctx, 'emergencyResponseInitiated')
  ) {
    addField(ctx, 'emergencyResponseInitiated');
    const map = {
      supervisorNotified: 'Supervisor notified.',
      teammateContacted911: 'Team member contacted 911.',
      emergencyServicesDispatched: 'Emergency services dispatched.',
    };
    txt.push(...mapValuesToText(emergencyResponseInitiated, map));
  }

  if (
    safetySupportProvided?.length &&
    !hasField(ctx, 'safetySupportProvided')
  ) {
    addField(ctx, 'safetySupportProvided');
    const map = {
      encouragedSaferEnvironment: 'Safer environment encouraged.',
      encouragedSupportPersonPresence: 'Support person presence encouraged.',
      encouragedDistancingFromMeans: 'Distancing from means encouraged.',
      notAppropriateNotPossible: 'Safety support not appropriate or possible.',
    };
    txt.push(...mapValuesToText(safetySupportProvided, map));
  }

  if (callOutcome && !hasField(ctx, 'callOutcome')) {
    addField(ctx, 'callOutcome');
    const map = {
      emergencyServicesArrived: 'Emergency services arrived.',
      callTransferred: 'Call transferred.',
      callDisconnectedPriorToArrival: 'Call disconnected prior to arrival.',
    };
    const line = map[callOutcome];
    if (line) txt.push(line);
  }

  return txt.join(' ');
};
